<template>
  <ClientOnly>
    <img v-if="computedProps && computedProps.requireComputeSize"
        data-load-type="pre-compute"
        :key="'compute@' + computedProps.src"
        :src="useNuxtApp().$lazylodImageUrl"
        :class="computedProps.class"
        :style="computedProps.style"
        :alt="computedProps.alt"
        @load="computeSize"
        />
    <img v-else-if="computedProps && computedProps.lazy"
        data-load-type="lazy"
        :key="'lazy@' + computedProps.src"
        :src="useNuxtApp().$lazylodImageUrl"
        :data-src="computedProps.src + computedProps.srcSurfix"
        :data-try-src="computedProps.src + computedProps.srcSurfix"
        :data-computed-size="computedProps.size.width + 'x' + computedProps.size.height"
        :class="computedProps.class"
        :style="computedProps.style"
        :alt="computedProps.alt"
        :usemap="usemap"
        :onerror="`this.src='${errorSrc}';this.onerror=undefined`"
        @load="onload"
        @click="(e) => emits('click', e)"
        @dblclick="(e) => emits('dblclick', e)"
        @mousedown="(e) => emits('mousedown', e)"
        @mouseup="(e) => emits('mouseup', e)"
        @mouseenter="(e) => emits('mouseenter', e)"
        @mouseover="(e) => emits('mouseover', e)"
        @mouseout="(e) => emits('mouseout', e)"
        @mouseleave="(e) => emits('mouseleave', e)"
        />
    <img v-else-if="computedProps"
        data-load-type="just"
        :key="computedProps.src"
        :src="computedProps.src + computedProps.srcSurfix"
        :data-try-src="computedProps.src + computedProps.srcSurfix"
        :data-computed-size="computedProps.size.width + 'x' + computedProps.size.height"
        :class="computedProps.class"
        :style="computedProps.style"
        :alt="computedProps.alt"
        :usemap="usemap"
        :onerror="`this.src='${errorSrc}';this.onerror=undefined`"
        @load="onload"
        @click="(e) => emits('click', e)"
        @dblclick="(e) => emits('dblclick', e)"
        @mousedown="(e) => emits('mousedown', e)"
        @mouseup="(e) => emits('mouseup', e)"
        @mouseenter="(e) => emits('mouseenter', e)"
        @mouseover="(e) => emits('mouseover', e)"
        @mouseout="(e) => emits('mouseout', e)"
        @mouseleave="(e) => emits('mouseleave', e)"/>
  </ClientOnly>
</template>

<script lang="ts">
  import { useNuxtApp } from '#app';


/**
 * image를 표시하는 컴포넌트
 * size는 확정된 숫자로 지정하는 것이 좋으나, 필요에 의해서 %로 지정할 수도 있습니다.
 * 단, %로 지정하면 정식 리사이징 처리가 되지 않으므로, 이미지의 품질이 떨어질 수 있습니다.
 * @author pat
 */

  interface ComImageProps {
    src?: string | null;
    at?: 'cdn';
    size?: string | number  ;
    fit?: 'cover' | 'contain';
    alt?: string;
    class?: HTMLAttributes['class'];
    updateable?: boolean | 'true' | 'false';
    usemap?: string;
    lazy?: any;
  }
  
  interface ImageSize {
    width?: number;
    height?: number;
  }

  interface ComImageComputed {
    src?: string;
    srcSurfix: string;
    style: string;
    alt?: string;
    class?: string;
    lazy: boolean;
    size: ImageSize;
    requireComputeSize: boolean;
    applyResize?: 'CONTENTS' | 'M2';
  }

</script>

<script lang="ts" setup>
import type { HTMLAttributes } from 'vue';

  const emits     = defineEmits(['click', 'dblclick', 'mousedown', 'mouseup', 'mouseenter', 'mouseleave', 'mouseover', 'mouseout', 'load']);
  const props     = defineProps<ComImageProps>();
  const errorSrc  = cu.getFileFullUrl('/images/system/noImage.jpg');
  const vpx       = useNuxtApp().$vpx;
  let   imgElm:HTMLImageElement|undefined = undefined;
  const parentElm:Ref<HTMLElement|undefined> = ref();
  const windowSize      = useState('_windowInnerSize');
  const compute         = (arg1:any, arg2?:any): ComImageComputed => {
    let   computeStyle        = '';
    const sizeInfo:ImageSize  = {width: undefined, height: undefined};
    let   sizeComputed        = true;
    let   resizeSurfix        = '';
    let   applyResize: 'CONTENTS' | 'M2' | undefined;
    if (props.size != null) {
      if (typeof(props.size) == 'number') {
        sizeInfo.width  = props.size;
        sizeInfo.height = props.size;
      } else if (props.size == 'compute') {
        if (parentElm.value) {
          if (parentElm.value.clientWidth != 0 && parentElm.value.clientHeight != 0) {
            sizeInfo.width  = parentElm.value.clientWidth;
            sizeInfo.height = parentElm.value.clientHeight;
            computeStyle    += `width:${sizeInfo.width}px;height:${sizeInfo.height}px;`;
          } else {
            const clientSize = Math.max(parentElm.value.clientWidth, parentElm.value.clientHeight);
            if (clientSize != 0) {
              sizeInfo.width  = clientSize;
              sizeInfo.height = clientSize;
              computeStyle    += `width:${sizeInfo.width}px;height:${sizeInfo.height}px;`;
            }
          }
          sizeComputed  = true;
        } else {
          sizeComputed  = false;
        }
      } else {
        const mch = props.size.match(/^(\d+)x(\d+)$/i);
        if (mch) {
          if (mch[1].endsWith('%')) {
            computeStyle    += `width:${mch[1]};`
          } else if (mch[1].endsWith('px')) {
            sizeInfo.width  = parseInt(mch[1]);
          } else {
            sizeInfo.width  = parseInt(mch[1]) * vpx;
          }
          if (mch[2].endsWith('%')) {
            computeStyle    += `height:${mch[2]};`
          } else if (mch[2].endsWith('px')) {
            sizeInfo.height = parseInt(mch[2]);
          } else {
            sizeInfo.height = parseInt(mch[2]) * vpx;
          }
        } else {
          if (props.size.endsWith('%')) {
            computeStyle    += `width:${props.size};height:${props.size};`;
          } else {
            const nsize = parseInt(props.size);
            if (!isNaN(nsize)) {
              sizeInfo.width  = nsize;
              sizeInfo.height = sizeInfo.width;          
            }
          }
        }
        if (sizeInfo.width !== undefined) {
          computeStyle    += `width:${sizeInfo.width}px;`
        }
        if (sizeInfo.height !== undefined) {
          computeStyle    += `height:${sizeInfo.height}px;`
        }
      }
    }
    let computeSrc:string|undefined;
    if (props.src == null) {
      computeSrc = undefined;
    } else {
      const smallCaseSrc  = props.src.toLowerCase();
      let   isApplyResize = false;
      if (smallCaseSrc.startsWith('http://') || smallCaseSrc.startsWith('https://') || smallCaseSrc.startsWith('//')) {
        computeSrc  = props.src;
        if (computeSrc.startsWith('//')) {
          computeSrc = 'https:' + computeSrc;
        }
        const srcs = smallCaseSrc.split("/");
        if (srcs.length > 2) { 
          const host = srcs[2];
          if (!host.endsWith("ezwel.com") && !host.endsWith("ezwelesp.com") && !host.endsWith("ezwelfare.net") && !host.includes('ridicdn')) {
            computeSrc = 'https://optz.ezwel.com/imgUrl=' + computeSrc;
          }
          if (host.includes('ridicdn')) { // ebook(리디북스) 1day TTL, M2 origin
            computeSrc = 'https://contents-1day-dev.ezwelfare.net/imgUrl=' + computeSrc + '/hdims/optimize/';
          }
          if (host.endsWith("ezwelfare.net")) {
            isApplyResize = true;          
          }
        }
      } else if (smallCaseSrc.startsWith('data:')) {
        computeSrc = props.src;
      } else {
        const isAtCdn = props.at == 'cdn';
        computeSrc = cu.getFileFullUrl(props.src, isAtCdn);
        if (isAtCdn) {
          isApplyResize = true;
        }
      }
      if (isApplyResize) {
        if (sizeInfo.width !== undefined && sizeInfo.height !== undefined) {
          resizeSurfix = `/gio/resize/${sizeInfo.width}x${sizeInfo.height}`;
        } else if (sizeInfo.width !== undefined) {
          resizeSurfix = `/gio/resize/${sizeInfo.width}x${sizeInfo.width}`;
        } else if (sizeInfo.height !== undefined) {
          resizeSurfix = `/gio/resize/${sizeInfo.height}x${sizeInfo.height}`;
        }
      }
    }
    computeStyle += `object-fit:${props.fit === undefined ? 'cover' : props.fit};`;

    return {
      src: computeSrc,
      srcSurfix: resizeSurfix,
      style: computeStyle,
      alt: props.alt,
      class: props.class,
      lazy:  props.lazy === undefined || props.lazy == 'true' || props.lazy == true,
      size: sizeInfo,
      requireComputeSize: !sizeComputed,
    }
  }
  const computedProps   = import.meta.server ? undefined : props.updateable == true || props.updateable == 'true' ? computed(() => compute(parentElm.value, props)) : computed(() => compute(parentElm.value));
  const onload          = (e:Event) => {
    const timg  = e.target as HTMLImageElement;
    imgElm      = timg;
    const cp    = unref(computedProps);
    if (cp && cp.src) {
      const isLoaded  = imgElm.src == imgElm.dataset.trySrc;
      if (timg.src == cp.src + cp.srcSurfix) {
        timg.removeAttribute('data-try-src');
        if (cp.srcSurfix != '' && cp.size.width !== undefined && cp.size.height !== undefined) {
          if (props.fit != 'contain') { // cover일때
            if (timg.naturalWidth < cp.size.width) {
              const toHeight  = Math.round(timg.naturalHeight * (cp.size.width / timg.naturalWidth));
              timg.src  = cp.src + `/gio/resize/${cp.size.width}x${toHeight}`;
            } else if (timg.naturalHeight < cp.size.height) {
              const toWidth  = Math.round(timg.naturalWidth * (cp.size.height / timg.naturalHeight));
              timg.src  = cp.src + `/gio/resize/${toWidth}x${cp.size.height}`;
            }
          }
        }
      }
      if (isLoaded) {
          emits('load', e);
      }
    }
  }
  const computeSize = (ev:any) => {
    imgElm = ev.target;
    if (parentElm.value == undefined && imgElm && imgElm.parentElement != null) {
      parentElm.value  = imgElm.parentElement;
    }
  }

  if (props.size == 'compute') {
    watch(windowSize, () => {
      if (props.size == 'compute' && imgElm && parentElm.value && computedProps && computedProps.value) {
        if (parentElm.value.clientWidth != 0 && parentElm.value.clientHeight != 0) {
          imgElm.style.width = parentElm.value.clientWidth + 'px';
          imgElm.style.height = parentElm.value.clientHeight + 'px';
        } else {
          const clientSize = Math.max(parentElm.value.clientWidth, parentElm.value.clientHeight);
          if (clientSize != 0) {
            imgElm.style.width = clientSize + 'px';
            imgElm.style.height = clientSize + 'px';
          }
        }
      }
    })
  }
  
</script>


<style>

</style>
